<template>
  <main-template>
    <v-container>
      <v-row align="center" class="mb-1">
        <v-col>
          <h1>
            <v-btn color="primary" x-small text @click="$router.go(-1)">
              <v-icon left>fa fa-angle-left</v-icon>
            </v-btn>
            Datas comemorativas
          </h1>
          <div class="pagina-descricao">
            Em Carnaval, Páscoa, Festa Junina, Halloween, Natal e Ano Novo, o sistema ganha cores,
            uma saudação na tela inicial e confetes temáticos. Relatórios e PDFs não mudam.
          </div>
        </v-col>
      </v-row>

      <v-card outlined class="config-card mb-4">
        <v-card-text>
          <div class="d-flex align-center">
            <div class="flex-grow-1">
              <div class="config-titulo">Para você</div>
              <div class="config-descricao">
                Vale para a sua conta, em qualquer aparelho em que você entrar.
              </div>
            </div>
            <v-switch
              v-model="minhaPreferencia"
              inset
              hide-details
              class="mt-0"
              :disabled="!liberadoPeloAdmin || salvandoMinha"
              :loading="salvandoMinha"
              aria-label="Mostrar datas comemorativas para mim"
              @change="salvarMinhaPreferencia"
            />
          </div>
          <v-alert v-if="!liberadoPeloAdmin" type="info" text dense class="mt-3 mb-0">
            O administrador desativou as datas comemorativas para o seu perfil.
          </v-alert>
        </v-card-text>
      </v-card>

      <v-card v-if="ehAdmin" outlined class="config-card mb-4">
        <v-card-text>
          <div class="d-flex align-center">
            <div class="flex-grow-1">
              <div class="config-titulo">Para todos da organização</div>
              <div class="config-descricao">
                Desligado, ninguém vê os temas, mesmo quem deixou ligado para si.
              </div>
            </div>
            <v-switch
              v-model="paraTodos"
              inset
              hide-details
              class="mt-0"
              :disabled="carregandoEmpresa || salvandoEmpresa"
              :loading="carregandoEmpresa || salvandoEmpresa"
              aria-label="Datas comemorativas para todos da organização"
              @change="salvarParaTodos"
            />
          </div>
          <div v-if="ligadoParcial" class="config-descricao mt-3">
            Hoje está ligado só para: {{ perfisLigadosTexto }}. Para escolher por perfil, use
            <router-link :to="{ name: 'administracao.configuracoes.modulos' }">
              Módulos por perfil
            </router-link>
          </div>
        </v-card-text>
      </v-card>

      <v-card outlined class="config-card">
        <v-card-text>
          <div class="config-titulo mb-2">Datas de {{ ano }}</div>
          <div v-for="data in datas" :key="data.chave" class="data-linha">
            <v-icon color="var(--brand)" class="mr-3">{{ data.icone }}</v-icon>
            <div class="flex-grow-1">{{ data.nome }}</div>
            <v-chip v-if="data.chave === dataDeHoje" x-small color="success" class="mr-3">
              Agora
            </v-chip>
            <div class="data-periodo">{{ formatar(data.inicio) }} a {{ formatar(data.fim) }}</div>
            <v-btn
              small
              text
              color="primary"
              class="ml-2"
              :aria-label="`Ver a celebração de ${data.nome}`"
              @click="verCelebracao(data)"
            >
              <v-icon left small>mdi-party-popper</v-icon>
              Ver
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-container>
  </main-template>
</template>

<script>
import { mapGetters } from "vuex";
import {
  MODULO,
  dataComemorativaDeHoje,
  datasDoAno,
  preferenciaLigada,
} from "@/plugins/datasComemorativas";
import { hojeBrasilia } from "@/utils/dataBrasilia";
import { celebrar } from "@/plugins/confete";

const PERFIS = ["colaborador", "lider", "admin"];
const NOMES_PERFIS = { colaborador: "colaboradores", lider: "líderes", admin: "administradores" };
const ROLES_ADMIN = ["admin", "super-admin", "TI"];

export default {
  name: "DatasComemorativasPage",
  data() {
    const ano = Number(hojeBrasilia().slice(0, 4));
    return {
      ano,
      datas: datasDoAno(ano),
      dataDeHoje: dataComemorativaDeHoje()?.chave || null,
      minhaPreferencia: true,
      salvandoMinha: false,
      perfisLigados: PERFIS,
      paraTodos: true,
      carregandoEmpresa: false,
      salvandoEmpresa: false,
    };
  },
  computed: {
    ...mapGetters("Auth", ["user"]),
    ...mapGetters("AppConfigurations", ["moduloAtivo"]),
    liberadoPeloAdmin() {
      return this.moduloAtivo(MODULO);
    },
    ehAdmin() {
      const roles = (this.user?.roles || []).map((r) => r.name);
      if (this.user?.roleName) roles.push(this.user.roleName);
      return roles.some((r) => ROLES_ADMIN.includes(r));
    },
    ligadoParcial() {
      return this.perfisLigados.length > 0 && this.perfisLigados.length < PERFIS.length;
    },
    perfisLigadosTexto() {
      return this.perfisLigados.map((p) => NOMES_PERFIS[p]).join(", ");
    },
  },
  watch: {
    user: {
      immediate: true,
      handler(user) {
        this.minhaPreferencia = preferenciaLigada(user);
      },
    },
  },
  mounted() {
    if (this.ehAdmin) this.carregarEmpresa();
  },
  methods: {
    async salvarMinhaPreferencia(ativo) {
      this.salvandoMinha = true;
      try {
        await this.$services.authService.atualizarDatasComemorativas(ativo);
        this.$toast.success(
          ativo
            ? "Datas comemorativas ligadas para você."
            : "Datas comemorativas desligadas para você."
        );
      } catch (error) {
        this.minhaPreferencia = !ativo;
        this.$handleError(error);
      } finally {
        this.salvandoMinha = false;
      }
    },
    async carregarEmpresa() {
      this.carregandoEmpresa = true;
      try {
        const dados = await this.$services.appConfigurationService.getModulos();
        this.perfisLigados = dados.configuracao?.[MODULO] || PERFIS;
        this.paraTodos = this.perfisLigados.length > 0;
      } catch (error) {
        this.$handleError(error);
      } finally {
        this.carregandoEmpresa = false;
      }
    },
    async salvarParaTodos(ligado) {
      this.salvandoEmpresa = true;
      try {
        const perfis = ligado ? PERFIS : [];
        const dados = await this.$services.appConfigurationService.salvarModulos({
          modulos: { [MODULO]: perfis },
        });
        this.perfisLigados = dados.configuracao?.[MODULO] || perfis;
        await this.$store.dispatch("AppConfigurations/fetchModulos");
        this.$toast.success(
          ligado
            ? "Datas comemorativas ligadas para todos."
            : "Datas comemorativas desligadas para todos."
        );
      } catch (error) {
        this.paraTodos = !ligado;
        this.$handleError(error);
      } finally {
        this.salvandoEmpresa = false;
      }
    },
    // Pré-visualização: funciona mesmo fora do período e com o tema desligado.
    verCelebracao(data) {
      celebrar(data);
    },
    formatar(data) {
      const [, mes, dia] = data.split("-");
      return `${dia}/${mes}`;
    },
  },
};
</script>

<style scoped>
.pagina-descricao {
  font-size: 14px;
  color: var(--text-muted);
  max-width: 760px;
}

.config-card {
  border-radius: 12px !important;
}

.config-titulo {
  font-size: 16px;
  font-weight: 700;
  color: var(--text);
}

.config-descricao {
  font-size: 13px;
  color: var(--text-muted);
}

.data-linha {
  display: flex;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid var(--border-soft);
}

.data-linha:last-child {
  border-bottom: none;
}

.data-periodo {
  font-size: 13px;
  color: var(--text-muted);
  min-width: 110px;
  text-align: right;
}
</style>
